import { isValidStateCode } from "../data/licensePlateStates.js";

function normalizeCode(code) {
  return String(code || "").toUpperCase();
}

function spotTime(spot) {
  return spot?.spottedAt ? new Date(spot.spottedAt).getTime() : 0;
}

function tripStartMs(trip) {
  return new Date(trip.startDate + "T00:00:00").getTime();
}

function tripEndMs(trip) {
  return new Date(trip.endDate + "T23:59:59").getTime();
}

function isWithinTripDates(spot, trip) {
  if (!trip?.startDate || !trip?.endDate) return true;
  const t = spotTime(spot);
  return t >= tripStartMs(trip) && t <= tripEndMs(trip);
}

function tripsById(trips) {
  const map = {};
  for (const trip of trips || []) map[trip.id] = trip;
  return map;
}

function tripCountsForAllTime(trip) {
  return trip?.countForAllTime !== false;
}

function tripCountsForOtherTrips(trip) {
  return trip?.countForOtherTrips !== false;
}

export function spotCountsForAllTime(spot, tripMap) {
  if (spot?.general === true) return true;
  const sourceId = spot?.sourceTripId;
  if (!sourceId) return true;
  const sourceTrip = tripMap[sourceId];
  if (!sourceTrip) return true;
  return tripCountsForAllTime(sourceTrip);
}

export function spotCountsForTrip(spot, targetTrip, tripMap) {
  const sourceId = spot?.sourceTripId;
  if (!sourceId) return true;
  if (sourceId === targetTrip.id) return true;
  const sourceTrip = tripMap[sourceId];
  if (!sourceTrip) return true;
  return tripCountsForOtherTrips(sourceTrip);
}

function firstClaimPerState(spots) {
  const claims = {};
  const sorted = [...spots].sort((a, b) => spotTime(a) - spotTime(b));
  for (const spot of sorted) {
    const code = normalizeCode(spot.stateCode);
    if (!isValidStateCode(code) || claims[code]) continue;
    claims[code] = {
      stateCode: code,
      spottedBy: spot.spottedBy,
      spottedByName: spot.spottedByName || spot.spottedBy,
      spottedAt: spot.spottedAt
    };
  }
  return claims;
}

function tripEligibleSpots(spots, trip, tripMap) {
  const participantSet = new Set(trip.participantIds || []);
  return (spots || []).filter(
    (s) => !s.general
      && participantSet.has(s.spottedBy)
      && isWithinTripDates(s, trip)
      && spotCountsForTrip(s, trip, tripMap)
  );
}

export function computeTripClaims(spots, trip, trips) {
  if (!trip) return {};
  const tripMap = tripsById(trips || [trip]);
  return firstClaimPerState(tripEligibleSpots(spots, trip, tripMap));
}

function computeTripLeaderboard(claims) {
  const scores = {};
  for (const claim of Object.values(claims || {})) {
    const id = claim.spottedBy;
    if (!id) continue;
    if (!scores[id]) {
      scores[id] = { spottedBy: id, spottedByName: claim.spottedByName, score: 0 };
    }
    scores[id].score += 1;
  }
  return Object.values(scores).sort(
    (a, b) => b.score - a.score || (a.spottedByName || "").localeCompare(b.spottedByName || "")
  );
}

export function historyToSpots(history) {
  return (history || []).map((row) => ({
    stateCode: row.stateCode,
    spottedBy: row.spottedBy,
    spottedByName: row.spottedByName,
    spottedAt: row.spottedAt,
    general: row.general,
    sourceTripId: row.sourceTripId || null
  }));
}

export function tripsCreditedForSpot(history, row, trips) {
  if (row?.general) return [];
  const spots = historyToSpots(history);
  const code = normalizeCode(row.stateCode);
  return (trips || []).filter((trip) => {
    const claims = computeTripClaims(spots, trip, trips);
    const claim = claims[code];
    if (!claim) return false;
    return claim.spottedBy === row.spottedBy && claim.spottedAt === row.spottedAt;
  });
}

export function getTripLeader(history, trip, trips) {
  const spots = historyToSpots(history);
  const claims = computeTripClaims(spots, trip, trips);
  const board = computeTripLeaderboard(claims);
  return board[0] || null;
}

export function isTripActive(trip, refDate = new Date()) {
  if (!trip?.startDate || !trip?.endDate) return false;
  const refMs = refDate.getTime();
  return refMs >= tripStartMs(trip) && refMs <= tripEndMs(trip);
}

export function sortTripsForDisplay(trips) {
  return [...(trips || [])].sort((a, b) => {
    const aActive = isTripActive(a);
    const bActive = isTripActive(b);
    if (aActive !== bActive) return aActive ? -1 : 1;
    return (b.endDate || "").localeCompare(a.endDate || "");
  });
}

/** Most recently ended trips first; used to cap history to the last N trips. */
export function getLastNTrips(trips, limit = 4) {
  return [...(trips || [])]
    .sort((a, b) => {
      const byEnd = (b.endDate || "").localeCompare(a.endDate || "");
      if (byEnd !== 0) return byEnd;
      return (b.createdAt || "").localeCompare(a.createdAt || "");
    })
    .slice(0, limit);
}

/**
 * Trip-level spottings for a state within the last `recentTripLimit` trips.
 * When `excludeTripId` is set (current map trip), that trip is omitted from results.
 */
export function getStateTripSpottingHistory(stateCode, history, trips, options = {}) {
  const { excludeTripId, recentTripLimit = 4 } = options;
  const code = normalizeCode(stateCode);
  const spots = historyToSpots(history);
  const recentTrips = getLastNTrips(trips, recentTripLimit);

  return recentTrips
    .filter((trip) => trip.id !== excludeTripId)
    .map((trip) => {
      const claims = computeTripClaims(spots, trip, trips);
      const claim = claims[code];
      if (!claim) return null;
      return { trip, claim };
    })
    .filter(Boolean);
}
